import { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  StickyNote,
  Send,
  Loader2,
  Trash2,
  X,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";

/* RecordActionsMenu — the per-row dropdown on the Vendor List + Couple List
 * that opens a side drawer with two tools:
 *   1. Consultation notes timeline (add new + delete old, newest-first)
 *   2. Send a document from the library to this record's email
 *
 * Props:
 *   - kind: "vendor" | "couple"
 *   - targetId: the application row's `id`
 *   - email: row email (defaults to recipient in the send-doc form)
 *   - title: human-readable name shown in the drawer heading
 */

const NotesTab = ({ kind, targetId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/notes/${kind}/${targetId}`);
      setNotes(r.data || []);
    } catch {
      toast("Couldn't load notes", { duration: 2200 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Reload only when the target record changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, targetId]);

  const add = async (e) => {
    e.preventDefault();
    const v = text.trim();
    if (!v) return;
    setSaving(true);
    try {
      const r = await api.post(`/admin/notes/${kind}/${targetId}`, { text: v });
      setNotes((s) => [r.data, ...s]);
      setText("");
    } catch (err) {
      toast(err?.response?.data?.detail || "Couldn't save note", { duration: 2400 });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/notes/${id}`);
      setNotes((s) => s.filter((n) => n.id !== id));
    } catch {
      toast("Delete failed", { duration: 2200 });
    }
  };

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      {/* New-note form */}
      <form onSubmit={add} className="flex flex-col gap-2">
        <label
          className="text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]"
          htmlFor="record-note-text"
        >
          Add note
        </label>
        <textarea
          id="record-note-text"
          data-testid="record-note-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="e.g. Free consultation Mon @ 14:30 — confirmed budget 35k, sent shortlist"
          className="px-3 py-2 text-sm bg-white border border-[var(--ld-ink)]/25 resize-y"
        />
        <button
          type="submit"
          disabled={saving || !text.trim()}
          data-testid="record-note-save"
          className="self-end inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-[0.22em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] hover:bg-black disabled:opacity-60"
        >
          {saving && <Loader2 size={12} className="animate-spin" />}
          Save note
        </button>
      </form>

      {/* Timeline */}
      <div className="border-t border-[var(--ld-border)]/60 pt-3 overflow-y-auto flex-1 min-h-0">
        {loading && (
          <p className="text-sm text-[var(--ld-ink-muted)]">Loading notes…</p>
        )}
        {!loading && notes.length === 0 && (
          <p
            data-testid="record-notes-empty"
            className="italic text-[13px] text-[var(--ld-ink-muted)]"
          >
            No consultation notes yet.
          </p>
        )}
        <ul className="space-y-3" data-testid="record-notes-list">
          {notes.map((n) => (
            <li
              key={n.id}
              data-testid={`record-note-${n.id}`}
              className="border border-[var(--ld-border)] bg-white p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="font-accent text-[10px] tracking-[0.22em] uppercase text-[var(--ld-gold-deep)]">
                  {new Date(n.created_at).toLocaleString()}
                </div>
                <button
                  type="button"
                  data-testid={`record-note-delete-${n.id}`}
                  onClick={() => remove(n.id)}
                  className="text-rose-500 hover:text-rose-700"
                  aria-label="Delete note"
                >
                  <Trash2 size={12} />
                </button>
              </div>
              <p className="mt-2 text-sm text-[var(--ld-ink)] whitespace-pre-wrap break-words">
                {n.text}
              </p>
              {n.author_email && (
                <p className="mt-1 text-[10px] text-[var(--ld-ink-muted)]">
                  — {n.author_email}
                </p>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const SendDocTab = ({ email, kind }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [recipient, setRecipient] = useState(email || "");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/documents");
        if (!cancelled) setDocs(r.data || []);
      } catch {
        if (!cancelled) toast("Couldn't load documents", { duration: 2200 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = docs.filter((d) => {
    // Audience filter — only show docs tagged for this kind or "either"
    if (d.audience !== "either" && d.audience !== kind) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (d.title || "").toLowerCase().includes(s);
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedId || !recipient.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/documents/send", {
        document_id: selectedId,
        recipient_email: recipient.trim(),
        message: message.trim() || undefined,
      });
      toast(
        data.sent
          ? `Sent to ${data.recipient}`
          : `Queued — Resend not configured (${data.recipient})`,
        { duration: 2800 }
      );
      setSelectedId("");
      setMessage("");
    } catch (err) {
      toast(err?.response?.data?.detail || "Send failed", { duration: 2800 });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-4 flex-1 min-h-0">
      <label className="flex flex-col gap-1">
        <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
          Recipient email
        </span>
        <input
          type="email"
          required
          data-testid="record-send-email"
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder="recipient@example.com"
          className="h-10 px-3 text-sm bg-white border border-[var(--ld-ink)]/25"
        />
      </label>

      <div className="flex flex-col gap-1 flex-1 min-h-0">
        <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
          Choose a document
        </span>
        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ld-ink-muted)]" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search documents…"
            className="w-full h-9 bg-white border border-[var(--ld-ink)]/25 pl-9 pr-3 text-[13px]"
          />
        </div>
        <div className="border border-[var(--ld-border)] bg-white max-h-56 overflow-y-auto mt-2">
          {loading && (
            <p className="p-3 text-sm text-[var(--ld-ink-muted)]">Loading…</p>
          )}
          {!loading && filtered.length === 0 && (
            <p className="p-3 italic text-[13px] text-[var(--ld-ink-muted)]">
              {docs.length === 0
                ? "No documents in library yet."
                : "No documents match this audience / search."}
            </p>
          )}
          <ul data-testid="record-send-doc-list">
            {filtered.map((d) => (
              <li
                key={d.id}
                data-testid={`record-send-doc-${d.id}`}
                className={
                  "px-3 py-2 cursor-pointer border-l-2 " +
                  (selectedId === d.id
                    ? "bg-[var(--ld-gold)]/15 border-[var(--ld-gold-deep)]"
                    : "border-transparent hover:bg-[var(--ld-ivory-warm)]/40")
                }
                onClick={() => setSelectedId(d.id)}
              >
                <div className="font-medium text-[13px] text-[var(--ld-ink)]">
                  {d.title}
                </div>
                <div className="text-[11px] text-[var(--ld-ink-muted)]">
                  {d.filename} · {d.audience}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--ld-ink-muted)]">
          Personal note (optional)
        </span>
        <textarea
          rows={2}
          data-testid="record-send-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Lovely to chat earlier — see attached."
          className="px-3 py-2 text-sm bg-white border border-[var(--ld-ink)]/25 resize-y"
        />
      </label>

      <button
        type="submit"
        disabled={!selectedId || sending}
        data-testid="record-send-submit"
        className="self-end inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] hover:bg-black disabled:opacity-60"
      >
        {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} strokeWidth={1.7} />}
        {sending ? "Sending…" : "Send"}
      </button>
    </form>
  );
};

const Drawer = ({ open, onClose, title, children }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="record-actions-drawer"
      className="fixed inset-0 z-50 bg-[var(--ld-ink)]/60 backdrop-blur-sm flex justify-end"
    >
      <aside className="w-full max-w-md bg-[var(--ld-ivory)] border-l border-[var(--ld-border)] h-full flex flex-col">
        <header className="flex items-center justify-between gap-3 p-5 border-b border-[var(--ld-border)]">
          <div className="min-w-0">
            <span className="font-accent text-[10px] tracking-[0.25em] uppercase text-[var(--ld-gold-deep)]">
              Record actions
            </span>
            <h3 className="font-serif-display text-xl mt-1 text-[var(--ld-ink)] truncate">
              {title}
            </h3>
          </div>
          <button
            type="button"
            data-testid="record-actions-close"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-9 w-9 items-center justify-center border border-[var(--ld-ink)]/20 hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)]"
          >
            <X size={14} strokeWidth={1.6} />
          </button>
        </header>
        <div className="flex-1 min-h-0 overflow-hidden p-5">{children}</div>
      </aside>
    </div>
  );
};

const RecordActionsMenu = ({ kind, targetId, email, title }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("notes");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const choose = (which) => {
    setTab(which);
    setOpen(false);
    setDrawerOpen(true);
  };

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        data-testid={`record-actions-${kind}-${targetId}`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] tracking-[0.2em] uppercase border border-[var(--ld-ink)]/25 text-[var(--ld-ink)] hover:bg-[var(--ld-ink)] hover:text-[var(--ld-gold)] transition-colors"
      >
        Actions
        <ChevronDown size={12} strokeWidth={1.7} />
      </button>
      {open && (
        <div
          className="absolute right-0 z-30 mt-2 w-52 bg-[var(--ld-ink)] text-[var(--ld-gold)] border border-[var(--ld-gold-deep)]/30 shadow-[0_12px_32px_rgba(44,42,41,0.25)]"
          data-testid={`record-actions-menu-${kind}-${targetId}`}
        >
          <button
            type="button"
            data-testid={`record-actions-notes-${kind}-${targetId}`}
            onClick={() => choose("notes")}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-[11px] tracking-[0.2em] uppercase hover:bg-black hover:text-[#e9d59a]"
          >
            <StickyNote size={12} strokeWidth={1.6} />
            Consultation notes
          </button>
          <button
            type="button"
            data-testid={`record-actions-send-${kind}-${targetId}`}
            onClick={() => choose("send")}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-[11px] tracking-[0.2em] uppercase hover:bg-black hover:text-[#e9d59a] border-t border-white/10"
          >
            <Send size={12} strokeWidth={1.6} />
            Send a document
          </button>
        </div>
      )}

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={title}>
        {/* Tab switcher inside the drawer */}
        <div className="flex items-center gap-1 border-b border-[var(--ld-border)] mb-5">
          {[
            { id: "notes", label: "Notes" },
            { id: "send", label: "Send doc" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={`drawer-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={
                "relative px-4 py-2 text-[10px] tracking-[0.22em] uppercase transition-colors " +
                (tab === t.id
                  ? "text-[var(--ld-ink)]"
                  : "text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]")
              }
            >
              {t.label}
              {tab === t.id && (
                <span
                  aria-hidden
                  className="absolute left-0 right-0 -bottom-px h-[2px] bg-[var(--ld-gold-deep)]"
                />
              )}
            </button>
          ))}
        </div>
        {tab === "notes" ? (
          <NotesTab kind={kind} targetId={targetId} />
        ) : (
          <SendDocTab email={email} kind={kind} />
        )}
      </Drawer>
    </div>
  );
};

export default RecordActionsMenu;
